/**
 * Generic ↔ detailed, as a two-segment switch.
 *
 * Detailed is only offered where it can work - the site must have the key lifecycle
 * wired up and trust this host with it. When it cannot, the detailed segment is
 * disabled with a reason on hover rather than hidden, so it is clear the mode exists
 * and *why* it is unavailable here, instead of the control silently differing per card.
 */

import { KeyRound, Unlock } from 'lucide-react'

import type { TargetMode } from '../../api/types'
import { cn } from '../../lib/cn'
import { Tooltip } from './Tooltip'

interface ModeToggleProps {
  mode: TargetMode
  onChange: (mode: TargetMode) => void
  /** Why detailed is unavailable, or null if it is available. Disables the segment. */
  detailedDisabledReason: string | null
  pending?: boolean
}

export function ModeToggle({
  mode,
  onChange,
  detailedDisabledReason,
  pending,
}: ModeToggleProps) {
  return (
    <div
      className="inline-flex overflow-hidden rounded-md border text-[11px]"
      role="group"
      aria-label="Monitoring mode"
    >
      <button
        type="button"
        aria-pressed={mode === 'generic'}
        disabled={pending}
        onClick={() => onChange('generic')}
        className={cn(
          'inline-flex items-center gap-1 px-2 py-1 transition-colors disabled:opacity-60',
          mode === 'generic'
            ? 'bg-accent-soft font-semibold text-ink'
            : 'text-ink-secondary hover:bg-surface-sunken',
        )}
      >
        <Unlock size={11} aria-hidden />
        Generic
      </button>

      {detailedDisabledReason ? (
        <Tooltip content={detailedDisabledReason}>
          <span
            aria-disabled
            className="inline-flex cursor-help items-center gap-1 px-2 py-1 text-ink-muted"
          >
            <KeyRound size={11} aria-hidden />
            Detailed
          </span>
        </Tooltip>
      ) : (
        <button
          type="button"
          aria-pressed={mode === 'detailed'}
          disabled={pending}
          onClick={() => onChange('detailed')}
          className={cn(
            'inline-flex items-center gap-1 border-l px-2 py-1 transition-colors disabled:opacity-60',
            mode === 'detailed'
              ? 'bg-accent-soft font-semibold text-ink'
              : 'text-ink-secondary hover:bg-surface-sunken',
          )}
        >
          <KeyRound size={11} aria-hidden />
          Detailed
        </button>
      )}
    </div>
  )
}
